import React from 'react';
import Joi from 'joi';

import {
	api as apiRequest,
	batchApi as batchApiRequest
} from '@culture/common-helpers/request';
import { addCleanCache } from '@culture/common-helpers/params';
import {
	checkEntityStatus,
	getFullEntityUrl
} from '@culture/common-helpers/entities';
import { getBreadcrumbs } from 'logic/page';
import Template from 'pageTemplates/entities/streamingPlatforms';

const InstituteDetail = (props) => <Template {...props} />;

const broadcastFields = [
	'title',
	'name',
	'thumbnailFile',
	'publishDate',
	'institute',
	'toDate',
	'rubrics',
	'type'
];

const getInstitute = (params) => {
	const instituteParams = addCleanCache(
		{
			withBroadcastInfo: true,
			cached: true
		},
		params.cleanCache
	);

	return apiRequest(`institutes/${params._id}`, instituteParams);
};

const getEntityMeta = (cleanCache, institute) => {
	const entityMetaParams = addCleanCache(
		{
			_id: institute._id,
			entityType: 'institutes',
			cached: true
		},
		cleanCache
	);

	return apiRequest('meta/entity', entityMetaParams);
};

const getNearestBroadcastParams = (cleanCache, institute) =>
	addCleanCache(
		{
			statuses: ['published'],
			institutes: [institute._id],
			fields: broadcastFields.join(),
			sort: 'publishDate',
			isPassed: false,
			limit: 10,
			cached: true,
			isWasBroadcast: true
		},
		cleanCache
	);

const getPassedBroadcastParams = (cleanCache, institute) =>
	addCleanCache(
		{
			statuses: ['published'],
			institutes: [institute._id],
			isPassed: true,
			fields: broadcastFields.join(),
			sort: '-publishDate',
			limit: 10,
			cached: true,
			isWasBroadcast: true
		},
		cleanCache
	);

const getPopularBroadcastParams = (cleanCache, institute) =>
	addCleanCache(
		{
			statuses: ['published'],
			types: institute.broadcastsTypes,
			fields: ['broadcastType'].concat(broadcastFields).join(),
			sort: '-views',
			limit: 10,
			cached: true,
			isWasBroadcast: true
		},
		cleanCache
	);

const getStreamingPlatformsParams = (cleanCache, institute) =>
	addCleanCache(
		{
			statuses: ['published'],
			broadcastsTypes: institute.broadcastsTypes,
			excludeIds: [institute._id],
			fields: ['title', 'name', 'thumbnailFile', 'locale'].join(),
			limit: 10,
			sort: '-broadcastCount',
			cached: true
		},
		cleanCache
	);

const getBreadcrumbsParams = (cleanCache, institute) =>
	addCleanCache(
		{
			entityType: 'institutes',
			_id: institute._id,
			rubricPath: (institute.rubric && institute.rubric.path) || '',
			cached: true
		},
		cleanCache
	);

const getNearestBroadcasts = (params, institute) =>
	apiRequest(
		'broadcast',
		getNearestBroadcastParams(params.cleanCache, institute)
	);

const getPassedBroadcasts = (params, institute) =>
	apiRequest(
		'broadcast',
		getPassedBroadcastParams(params.cleanCache, institute)
	);

const getPopularBroadcasts = (params, institute) =>
	apiRequest(
		'broadcast',
		getPopularBroadcastParams(params.cleanCache, institute)
	);

const getPopularInstitutes = (params, institute) =>
	apiRequest(
		'streamingPlatforms',
		getStreamingPlatformsParams(params.cleanCache, institute)
	);

export async function getServerSideProps({ query, req }) {
	const validationSchema = Joi.object().keys({
		_id: Joi.number().integer().required(),
		name: Joi.array().items(Joi.string()).length(1),
		cleanCache: Joi.boolean()
	});

	const params = Joi.attempt(query, validationSchema, {
		stripUnknown: true
	});

	const institute = await getInstitute(params);

	if (!checkEntityStatus(institute)) {
		return {
			notFound: true
		};
	}

	if (!params.name || institute.name !== params.name[0]) {
		return {
			redirect: {
				destination: getFullEntityUrl(req.url, 'streamingPlatforms', institute),
				permanent: true
			}
		};
	}

	const [
		nearestBroadcasts,
		passedBroadcasts,
		popularBroadcasts,
		popularInstitutes,
		breadcrumbs,
		entityMeta
	] = await batchApiRequest([
		getNearestBroadcasts(params, institute),
		getPassedBroadcasts(params, institute),
		getPopularBroadcasts(params, institute),
		getPopularInstitutes(params, institute),
		getBreadcrumbs(getBreadcrumbsParams(params.cleanCache, institute)),
		getEntityMeta(params.cleanCache, institute)
	]);

	return {
		props: {
			institute,
			nearestBroadcasts: nearestBroadcasts.items,
			passedBroadcasts: passedBroadcasts.items,
			popularBroadcasts: popularBroadcasts.items,
			popularInstitutes: popularInstitutes.items,
			breadcrumbs,
			entityMeta
		}
	};
}

export default InstituteDetail;
